'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormData {
  email: string;
  password: string;
  remember: boolean;
}

const DEMO_EMAIL = 'marcus@cityhappenings.io';
const DEMO_PASSWORD = 'CityRun2026!';

export default function LoginForm() {
  const router = useRouter();
  const { signIn } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    defaultValues: { remember: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setAuthError('');
    setLoading(true);
    try {
      await signIn(data.email, data.password);
      router.push('/');
      router.refresh();
    } catch (err: any) {
      setLoading(false);
      setAuthError(err?.message || 'Invalid credentials. Please try again.');
    }
  };

  const fillDemo = () => {
    setValue('email', DEMO_EMAIL);
    setValue('password', DEMO_PASSWORD);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel — Brand */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary flex-col items-center justify-center p-12 relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 bg-white translate-x-20 -translate-y-20" />
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full opacity-10 bg-white -translate-x-20 translate-y-20" />
        <div className="absolute top-1/2 left-1/4 w-32 h-32 rounded-full opacity-5 bg-secondary" />

        <div className="relative z-10 text-center max-w-md">
          <div className="flex justify-center mb-6">
            <div className="bg-white/20 rounded-3xl p-4 backdrop-blur-sm">
              <AppLogo size={56} />
            </div>
          </div>
          <h1 className="text-4xl font-extrabold text-white mb-3 leading-tight">
            City Happenings
          </h1>
          <p className="text-xl text-white/80 font-medium mb-8">
            Your Local Content Engine.
          </p>

          <div className="space-y-4 text-left">
            {[
              { icon: 'MapPinIcon', text: 'Manage unlimited cities in one place' },
              { icon: 'BoltIcon', text: 'AI-powered Facebook posts, automatically' },
              { icon: 'CheckCircleIcon', text: 'Review, approve, and publish with ease' },
            ].map((item, i) => (
              <div key={`feature-${i}`} className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3">
                <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-secondary flex-shrink-0" />
                <span className="text-white/90 text-sm font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-background">
        <div className="w-full max-w-sm">
          {/* Mobile Logo */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            <div className="flex items-center gap-3 mb-2">
              <AppLogo size={40} />
              <span className="text-2xl font-bold text-foreground">City Happenings</span>
            </div>
            <p className="text-muted-foreground text-sm">Your Local Content Engine.</p>
          </div>

          <div className="lg:block hidden mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-1">Sign in</h2>
            <p className="text-muted-foreground text-sm">Welcome back! Enter your credentials to continue.</p>
          </div>

          {/* Auth Error */}
          {authError && (
            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 flex items-start gap-2">
              <Icon name="ExclamationCircleIcon" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-600 font-medium">{authError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {/* Email */}
            <div>
              <label className="label-text">Email Address</label>
              <input
                type="email"
                placeholder="you@cityhappenings.io"
                className="input-field"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Enter a valid email address',
                  },
                })}
              />
              {errors.email && <p className="error-text">{errors.email.message}</p>}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="label-text mb-0">Password</label>
                <Link href="/forgot-password" className="text-xs font-medium text-primary hover:text-primary/80 transition-colors">
                  Forgot Password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="input-field pr-11"
                  {...register('password', { required: 'Password is required' })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-1"
                >
                  <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
                </button>
              </div>
              {errors.password && <p className="error-text">{errors.password.message}</p>}
            </div>

            {/* Remember Me */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="remember"
                className="w-4 h-4 rounded accent-primary"
                {...register('remember')}
              />
              <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer">
                Keep me signed in
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary py-3.5 text-base flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                'Log In'
              )}
            </button>
          </form>

          {/* Sign Up link */}
          <p className="mt-6 text-center text-sm text-muted-foreground">
            New to City Happenings?{' '}
            <Link
              href="/subscribe"
              className="font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              Get Started
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}